<template>
  <login-page-frame class="login-shell" :class="'login-shell--' + variant">
      <section class="login-panel" :aria-label="profile.label">
        <div class="login-panel__form">
          <slot></slot>
          <p class="login-policy">
            登录即表示同意
            <router-link target="_blank" :to="{ name: 'contract', query: { type: 'service' } }">《注册协议》</router-link>
            <router-link target="_blank" :to="{ name: 'contract', query: { type: 'people' } }">《个人信息处理规则》</router-link>
          </p>
        </div>

        <aside class="login-intro">
          <span class="login-intro__eyebrow">{{ profile.eyebrow }}</span>
          <h2>{{ profile.headline[0] }}<br>{{ profile.headline[1] }}</h2>
          <p class="login-intro__description">{{ profile.description }}</p>
          <ul class="login-capabilities">
            <li v-for="item in capabilities" :key="item.title">
              <span class="login-capabilities__icon" aria-hidden="true"><i :class="item.icon"></i></span>
              <div><h3>{{ item.title }}</h3><p>{{ item.description }}</p></div>
            </li>
          </ul>
          <div class="login-intro__signature"><span></span>商译 · 智慧商业</div>
        </aside>
      </section>
  </login-page-frame>
</template>

<script>
import LoginPageFrame from './LoginPageFrame.vue'

const LOGIN_PROFILES = {
  agency: {
    label: '代理后台登录',
    eyebrow: '代理经营中心',
    headline: ['店铺有序管理', '账务清晰可查'],
    description: '管理授权店铺，查看经营与账户信息',
    capabilities: [
      { icon: 'el-icon-office-building', title: '店铺与协作', description: '按归属管理店铺' },
      { icon: 'el-icon-wallet', title: '账户与分润', description: '收支明细，清晰可查' },
      { icon: 'el-icon-data-analysis', title: '经营与数据', description: '掌握权限范围内的经营情况' }
    ]
  },
  merchant: {
    label: '商家端登录',
    eyebrow: '全渠道全场景数字经营',
    headline: ['让生意更简单', '让经营更有数'],
    description: '连接线上商城与线下门店，助力每一天的经营',
    capabilities: [
      { icon: 'el-icon-shopping-bag-2', title: '商城与订单', description: '线上线下，一体经营' },
      { icon: 'el-icon-present', title: '会员与营销', description: '连接客户，沉淀价值' },
      { icon: 'el-icon-data-analysis', title: '门店与数据', description: '多店协同，经营可见' }
    ]
  },
  admin: {
    label: '平台管理登录',
    eyebrow: '平台管理中心',
    headline: ['连接业务', '让协同更高效'],
    description: '统一管理业务与服务，掌握平台运营全貌',
    capabilities: [
      { icon: 'el-icon-user', title: '账号与权限', description: '分工清晰，协作有序' },
      { icon: 'el-icon-connection', title: '业务与服务', description: '连接商家，统一管理' },
      { icon: 'el-icon-data-analysis', title: '经营与数据', description: '洞察运营，辅助决策' }
    ]
  },
  store: {
    label: '门店端登录',
    eyebrow: '门店端 · 日常经营',
    headline: ['专注门店经营', '让服务更高效'],
    description: '使用门店账号，处理本店订单与日常业务',
    capabilities: [
      { icon: 'el-icon-tickets', title: '订单与履约', description: '跟进本店订单，做好每一次服务' },
      { icon: 'el-icon-goods', title: '商品与库存', description: '掌握门店商品与库存动态' },
      { icon: 'el-icon-user', title: '会员与服务', description: '服务门店顾客，连接日常经营' }
    ]
  },
  region: {
    label: '区域账号登录',
    eyebrow: '区域账号端 · 协同管理',
    headline: ['连接区域门店', '让管理更协同'],
    description: '使用区域账号，管理已授权的区域与门店',
    capabilities: [
      { icon: 'el-icon-map-location', title: '区域与门店', description: '聚焦所辖门店，协同区域经营' },
      { icon: 'el-icon-connection', title: '业务与协作', description: '按授权范围处理区域业务' },
      { icon: 'el-icon-data-analysis', title: '经营与数据', description: '汇总区域经营，掌握门店动态' }
    ]
  }
}

export default {
  name: 'LoginShell',
  components: { LoginPageFrame },
  props: {
    variant: { type: String, default: 'merchant' }
  },
  computed: {
    profile() { return LOGIN_PROFILES[this.variant] || LOGIN_PROFILES.merchant },
    capabilities() { return this.profile.capabilities }
  }
}
</script>

<style lang="less" scoped>
.login-panel {
  width: 860px;
  max-width: 100%;
  display: grid;
  grid-template-columns: 440px minmax(0, 1fr);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 16px 64px rgba(37, 80, 130, .1), 0 2px 8px rgba(37, 80, 130, .025);
  overflow: hidden;
  &__form { min-width: 0; padding: 44px 52px 38px; }
}
.login-policy {
  margin: 18px 0 0;
  color: #8894a5;
  font-size: 12px;
  line-height: 22px;
  a { color: #61748e; text-decoration: none; white-space: nowrap; }
  a:hover { color: #006aff; }
}
.login-intro {
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 48px 40px 38px;
  background: linear-gradient(145deg, #f7faff, #f1f7ff);
  &__eyebrow { color: #417ed4; font-size: 13px; letter-spacing: 2px; }
  h2 { margin: 18px 0 16px; color: #203c62; font-size: 30px; font-weight: 600; line-height: 1.55; letter-spacing: 1px; }
  &__description { margin: 0; color: #7a8da6; font-size: 13px; line-height: 24px; }
  &__signature { display: flex; align-items: center; gap: 10px; margin-top: auto; padding-top: 30px; color: #8da4c2; font-size: 12px; letter-spacing: 1px; }
  &__signature span { width: 24px; height: 2px; background: #98b9ec; }
}
.login-capabilities {
  list-style: none;
  margin: 32px 0;
  padding: 0;
  display: grid;
  gap: 24px;
  li { display: flex; align-items: center; gap: 14px; }
  &__icon { display: flex; align-items: center; justify-content: center; width: 40px; height: 40px; border-radius: 12px; flex: none; background: #e7f0ff; color: #3c80e3; }
  &__icon i { font-size: 21px; }
  h3 { margin: 0 0 5px; font-size: 14px; color: #496685; font-weight: 500; }
  p { margin: 0; color: #8597ae; font-size: 12px; line-height: 18px; }
}
.login-shell--store {
  .login-intro { background: linear-gradient(145deg, #f5fbfb, #eef8f8); }
  .login-intro__eyebrow, .login-capabilities__icon { color: #258a8a; }
  .login-capabilities__icon { background: #e4f2f2; }
}
.login-shell--region {
  .login-intro { background: linear-gradient(145deg, #f8f8ff, #f1f3ff); }
  .login-intro__eyebrow, .login-capabilities__icon { color: #6969c1; }
  .login-capabilities__icon { background: #ebebfb; }
}
.login-shell ::v-deep {
  .login-heading h1 { margin: 0; font-size: 24px; line-height: 34px; font-weight: 600; color: #202c40; }
  .login-heading p { margin: 8px 0 26px; font-size: 13px; line-height: 20px; color: #8a96a6; }
  .login-tabs { display: flex; align-items: center; gap: 26px; margin-bottom: 26px; }
  .login-tabs button, .login-tab-static { position: relative; border: 0; padding: 0 0 12px; color: #8894a5; background: transparent; font-family: inherit; font-size: 17px; line-height: 24px; }
  .login-tabs button { cursor: pointer; }
  .login-tabs .is-active { color: #006aff; font-weight: 600; }
  .login-tabs .is-active::after { position: absolute; bottom: 0; left: 50%; width: 28px; height: 3px; content: ''; transform: translateX(-50%); background: #006aff; border-radius: 2px; }
  .login-tabs button:focus-visible, .login-code-button:focus-visible { outline: 2px solid #006aff; outline-offset: 4px; border-radius: 2px; }
  .login-form .el-form-item { margin-bottom: 20px !important; }
  .login-form .el-form-item__label { height: auto !important; padding: 0 0 8px; font-weight: 400; font-size: 13px; line-height: 20px; white-space: nowrap; }
  .login-form .el-form-item__content { line-height: 44px; }
  .login-form .el-input__inner { width: 100%; height: 44px !important; padding: 0 14px; color: #26364d; border: 1px solid #dfe5ee; border-radius: 8px !important; font-size: 14px; background: #fff; }
  .login-form .el-input__inner:hover { border-color: #afc5e5; }
  .login-form .el-input__inner:focus { border-color: #006aff; box-shadow: 0 0 0 3px rgba(0, 106, 255, .06); }
  .login-form .el-input__inner::placeholder { color: #b0bac8; }
  .login-form .el-input__suffix { right: 12px; }
  .login-form .el-input__icon { line-height: 44px; }
  .login-form .el-input--suffix .el-input__inner { padding-right: 40px; }
  .login-mobile .el-input__inner { padding-left: 84px; }
  .login-mobile__prefix { display: inline-flex; align-items: center; justify-content: center; gap: 8px; width: 68px; height: 24px; margin: 10px 0; border-right: 1px solid #e7ebf2; color: #4b5c74; cursor: pointer; font-size: 13px; }
  .login-code .el-input__inner { padding-right: 112px; }
  .login-code-button { height: 44px; border: 0; padding: 0; color: #006aff; font-family: inherit; background: transparent; cursor: pointer; font-size: 13px; }
  .login-code-button:disabled { color: #a5afbd; cursor: default; }
  .login-links { display: flex; align-items: center; justify-content: space-between; margin-top: -4px; margin-bottom: 24px; font-size: 13px; line-height: 20px; }
  .login-links span { color: #8894a5; }
  .login-links a { color: #79899e; text-decoration: none; }
  .login-links a:hover { color: #006aff; }
  .login-role-help { margin: 20px 0 0; color: #8894a5; font-size: 12px; line-height: 22px; }
  .login-role-help a { color: #006aff; text-decoration: none; white-space: nowrap; }
  .login-role-help a:hover { text-decoration: underline; }
  .login-submit.el-button { width: 100%; height: 46px; border-radius: 8px; border-color: #006aff; background: #006aff; font-size: 16px; letter-spacing: 3px; box-shadow: 0 6px 14px rgba(0, 106, 255, .12); }
  .login-submit.el-button:hover, .login-submit.el-button:focus { background: #1677ff; border-color: #1677ff; }
  .login-submit.el-button.is-disabled { background: #79b2ff; border-color: #79b2ff; box-shadow: none; }
  .login-admin-form { margin-top: 32px; margin-bottom: 32px; }
  .login-captcha { max-width: 100%; overflow-x: auto; }
}
@media (min-width: 681px) and (max-height: 800px) {
  .login-panel__form { padding-top: 30px; padding-bottom: 28px; }
  .login-intro { padding-top: 34px; padding-bottom: 28px; }
  .login-capabilities { margin: 24px 0; gap: 20px; }
  .login-intro__signature { padding-top: 20px; }
  .login-shell ::v-deep .login-heading p { margin-bottom: 20px; }
  .login-shell ::v-deep .login-tabs { margin-bottom: 20px; }
}
@media (max-width: 960px) {
  .login-panel { width: 800px; grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr); }
  .login-panel__form { padding: 40px 36px 34px; }
  .login-intro { padding: 44px 32px 34px; }
  .login-intro h2 { font-size: 27px; }
}
@media (max-width: 680px) {
  .login-panel { display: block; width: 420px; }
  .login-panel__form { padding: 34px 28px; }
  .login-intro { display: none; }
}
@media (max-width: 360px) {
  .login-panel__form { padding: 30px 22px; }
}
</style>
